import axios from 'axios';
import { supabase } from '@/services/supabase';
import router from '@/router';
import { i18n } from '@/i18n';
import { useUserStore } from '@/store/userStore';
import { useToastStore } from '@/store/toastStore';
import { anonymizeData, isAnonymousMode } from '@/utils/anonymousMode';

// Set the default baseURL
axios.defaults.baseURL = import.meta.env.VITE_API_URL;

//Set up interceptor
axios.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }

    const method = (config.method || 'get').toLowerCase();
    if (isAnonymousMode() && ['post', 'patch', 'put', 'delete'].includes(method)) {
      const url = `${config.url || ''} ${config.baseURL || ''}`;
      const allowed = /switch-organization|switchOrganization|chatwoot/i.test(url);
      if (!allowed) {
        const error = new Error('anonymous_mode_read_only');
        error.blockedByAnonymousMode = true;
        return Promise.reject(error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 403 codes from authenticateUser that mean the session/org is gone and the user
// must re-authenticate (e.g. a super-admin removed them from their only/current
// org). Ordinary permission 403s (requireRole) carry no code and must NOT log the
// user out.
const SESSION_ENDED_CODES = [
  'NO_ORGANIZATION_MEMBERSHIPS',
  'ORGANIZATION_ACCESS_REVOKED',
  'USER_NOT_PROVISIONED',
];

// Guard so a burst of failing requests only triggers a single logout + redirect.
let handlingSessionEnd = false;

axios.interceptors.response.use(
  (response) => {
    if (isAnonymousMode() && response?.data && typeof response.data === 'object') {
      response.data = anonymizeData(response.data);
    }
    return response;
  },
  async (error) => {
    if (error?.blockedByAnonymousMode) {
      useToastStore().addToast({
        type: 'info',
        message: i18n.global.t('common.anonymous_read_only'),
      });
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const code = error.response?.data?.code;
    // Only react to the org/session-revoked 403 codes from authenticateUser (e.g. a
    // super-admin removed the user from their only/current org). A plain 401 is
    // deliberately NOT handled here: it is often transient (a server/Redis blip is
    // returned as 401 "Authentication failed"), and blindly logging out on any 401
    // would boot every active user on an infra hiccup. Ordinary permission 403s
    // (requireRole) carry no code and are ignored for the same reason.
    // NO_ORGANIZATION_MEMBERSHIPS is ambiguous: it fires both for a user who was
    // removed from their only org (had access → log them out) AND for a fresh user
    // who has no org yet (mid-onboarding → leave them alone; the router handles it).
    // We tell them apart by whether the store currently holds an org. The other
    // codes always mean lost access, so they need no such guard.
    const accessRevoked =
      status === 403 &&
      SESSION_ENDED_CODES.includes(code) &&
      (code !== 'NO_ORGANIZATION_MEMBERSHIPS' || !!useUserStore().currentOrganization);
    // Defensive exclusion for auth-flow pages. In practice these pages route their
    // calls through /public/* and the token-only accept-invite endpoint, so they
    // should never receive a SESSION_ENDED_CODES 403 — but if one ever fires here
    // (e.g. a future authenticated call added to the page) we must not boot the user
    // mid-flow. /login and /accept-invite are the only such pages today.
    const path = router.currentRoute.value.path;
    const onAuthFlowPage = path.startsWith('/login') || path.startsWith('/accept-invite');

    if (accessRevoked && !onAuthFlowPage && !handlingSessionEnd) {
      // Claim the guard synchronously (before any await) so a burst of parallel
      // failing requests — e.g. the several bootstrap calls fired on refresh —
      // only triggers ONE logout + toast + redirect, not one per request.
      handlingSessionEnd = true;

      // Only boot a user who actually has a session. A revoked-code 403 with no active
      // session is a normal pre-auth request (e.g. the accept-invite page checking
      // auth before the user logs in) — let the page handle its own redirect
      // instead of hijacking it and dropping the return path.
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        handlingSessionEnd = false;
        return Promise.reject(error);
      }

      try {
        try {
          await useUserStore().logout();
        } catch (logoutError) {
          // Redirect regardless — the session is already invalid server-side.
          console.error('Error during session-end logout:', logoutError);
        }
        // USER_NOT_PROVISIONED is an account-setup problem, not an org revocation,
        // so it gets its own message.
        const messageKey =
          code === 'USER_NOT_PROVISIONED'
            ? 'common.session_account_not_provisioned'
            : 'common.session_access_revoked';
        useToastStore().addToast({
          type: 'error',
          message: i18n.global.t(messageKey),
        });
        router.push('/login').catch(() => {});
      } finally {
        // Always release the guard — even if the toast or navigation threw — so a
        // one-off failure can't wedge it `true` and stop this tab booting on a later
        // session-end. The delay keeps the burst-dedup window intact.
        setTimeout(() => {
          handlingSessionEnd = false;
        }, 1000);
      }
    }

    return Promise.reject(error);
  }
);
